import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkle,
  Buildings,
  EnvelopeSimple,
  Phone,
  PaperPlaneRight,
  CheckCircle,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImprovementPlanRequestPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refToken = params.get('ref');

  const [form, setForm] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pre-fill from the referenced diagnostic order if available
  useEffect(() => {
    if (!refToken) return;
    fetch(`${API_URL}/api/client/service-requests/${refToken}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setForm((f) => ({
            ...f,
            name: d.name || '',
            organization: d.organization || '',
          }));
        }
      })
      .catch(() => {});
  }, [refToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (!confirm) {
      toast.error('يرجى تأكيد الطلب');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/service-requests/improvement-plan`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, confirm: true, ref_token: refToken }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'فشل الإرسال');
      }
      const data = await res.json();
      navigate(`/services/request/success?token=${data.access_token}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="improvement-plan-page"
      className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]"
      dir="rtl"
    >
      <section className="py-12 bg-[#F5F2EC]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-sm mb-4">
            <Sparkle size={14} weight="fill" />
            الخطوة التالية
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic']">
            طلب خطة تحسين
          </h1>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8">
            بناءً على نتائج التشخيص، نجهّز لك خطة تحسين مخصصة بما يتناسب مع
            أولويات منشأتك.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              الخدمة تشمل
            </h2>
            <ul className="space-y-2.5 mb-2">
              {[
                'تحديد الفجوات بدقة',
                'ترتيب الأولويات حسب الأثر',
                'خطة تنفيذ واضحة',
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-[#3D512D] text-sm"
                >
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="text-[#4A641F] shrink-0"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={submit}
            className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-6 sm:p-8"
          >
            <h2 className="text-lg font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic']">
              بيانات التواصل
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div>
                <Label htmlFor="name">الاسم الكامل *</Label>
                <Input
                  id="name"
                  data-testid="ip-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="organization" className="flex items-center gap-1.5">
                  <Buildings size={14} />
                  اسم المنشأة *
                </Label>
                <Input
                  id="organization"
                  data-testid="ip-org"
                  value={form.organization}
                  onChange={(e) =>
                    setForm({ ...form, organization: e.target.value })
                  }
                  required
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="email" className="flex items-center gap-1.5">
                  <EnvelopeSimple size={14} />
                  البريد الإلكتروني *
                </Label>
                <Input
                  id="email"
                  data-testid="ip-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  dir="ltr"
                  className="mt-1 rounded-xl text-right"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="flex items-center gap-1.5">
                  <Phone size={14} />
                  رقم الجوال (اختياري)
                </Label>
                <Input
                  id="phone"
                  data-testid="ip-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  dir="ltr"
                  className="mt-1 rounded-xl text-right"
                  placeholder="05xxxxxxxx"
                />
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="notes">ملاحظات إضافية (اختياري)</Label>
              <Textarea
                id="notes"
                data-testid="ip-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={4}
                placeholder="أي تفاصيل تساعدنا في تجهيز خطة مناسبة لأولوياتك"
                className="mt-1 rounded-xl"
              />
            </div>

            <label className="flex items-start gap-3 bg-[#F5F2EC] rounded-xl p-4 mb-6 cursor-pointer">
              <Checkbox
                data-testid="ip-confirm-checkbox"
                checked={confirm}
                onCheckedChange={setConfirm}
                className="mt-0.5"
              />
              <span className="text-sm text-[#1F3D2B] leading-7">
                أؤكّد رغبتي في الحصول على خطة تحسين مخصصة، وفريق آفاق الطفولة
                يقدر يتواصل معي للتفاصيل.
              </span>
            </label>

            <Button
              type="submit"
              data-testid="submit-improvement-plan"
              disabled={loading || !confirm}
              className="w-full bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-4 text-base font-bold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <PaperPlaneRight size={20} />
              {loading ? 'جاري الإرسال...' : 'تأكيد الطلب'}
            </Button>

            <p className="text-xs text-[#92A082] text-center mt-4 leading-7">
              لن يتم خصم أي مبلغ الآن — راح يتواصل معك فريقنا لإكمال التفاصيل
            </p>
          </form>
        </div>
      </section>
    </main>
  );
};

export default ImprovementPlanRequestPage;
